<template>
  <div>
    <div v-if="!dataLoaded">
      <v-progress-circular indeterminate color="primary" class="ret-center-absolute"/>
    </div>
    <div v-if="dataLoaded">
      <v-card class="ret-card-wide">
        <v-card-title>
          <h3 class="headline">
            {{ $t('future-workload') }}
          </h3>
          <v-spacer/>
          <v-tooltip bottom v-if="selectedAssetTypeId">
            <template v-slot:activator="{ on }">
              <v-btn dark color="primary" v-on="on" @click="buildAssetReportCsv">
                <v-icon>save_alt</v-icon> &ensp; {{ $tc('planned-issues.entity-name', 2) }}
              </v-btn>
            </template>
            {{ $t('export-issue-details-for-selected-asset-type') }}
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn dark color="primary" v-on="on" @click="buildSummaryReportCsv">
                <v-icon>save_alt</v-icon> &ensp; {{ $t('workload-statistics') }}
              </v-btn>
            </template>
            {{ $t('issues.export-between-selected-dates') }}
          </v-tooltip>
        </v-card-title>
        <v-card-text>
          <v-container fluid>
            <v-row>
              <v-col sm="1">
                <date-time-picker :labels="[$t('date-from')]" v-model="dateFrom" :show-time="false">
                  {{ $t('date-from') }}
                </date-time-picker>
              </v-col>
              <v-col sm="1">
                <date-time-picker :labels="[$t('date-to')]" v-model="dateTo" :show-time="false">
                  {{ $t('date-to') }}
                </date-time-picker>
              </v-col>
              <v-col sm="5">
                <v-autocomplete
                  v-model="selectedAssetTypeId"
                  :label="$t('asset-types.select')"
                  :items="assetTypesLocal"
                  item-text="name"
                  item-value="id"
                  prepend-inner-icon="filter_list"
                  clearable
                ></v-autocomplete>
              </v-col>
              <v-col sm="auto" class="text-right">
                <v-btn-toggle v-model="timespanFormat" exclusive>
                  <v-btn value="WW/YYYY">{{ $t('weekly') }}</v-btn>
                  <v-btn value="MM/YYYY">{{ $t('monthly') }}</v-btn>
                  <v-btn value="[Q]Q/YYYY">{{ $t('quarterly') }}</v-btn>
                </v-btn-toggle>
              </v-col>
            </v-row>
          </v-container>
          <common-future-workload-chart
            :date-from="dateFrom"
            :date-to="dateTo"
            :options="chartOptions"
            :chart-data="polishedChartData"
            :height="350"
          />
        </v-card-text>

        <v-card-text v-if="chartBarSelectedDataIndex !== null && chartBarSelectedDatasetIndex !== null">
          <issues-list
            :items="issuesToExport"
            show-asset
            day-dividers
          ></issues-list>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import displayHelpers from '../helpers/display-helpers.es6'
import CommonFutureWorkloadChart from './common-future-workload-chart'
import DateTimePicker from '../shared/components/date-time-picker.vue'
import IssuesList from '../issues/components/issues-list.vue'
import Moment from 'moment'
import { extendMoment } from 'moment-range'
import json2csv from 'json2csv'
import rgba from 'color-rgba'

const timespansPast = { 'weeks': 4, 'months': 3, 'quarters': 2 }
const timespansFuture = { 'weeks': 12, 'months': 12, 'quarters': 5 }
const moment = extendMoment(Moment)

export default {
  mixins: [displayHelpers],
  data () {
    return {
      to: null,
      auxModels: [{ name: 'asset-types' }],
      dateFrom: moment().date(1).subtract(timespansPast.months, 'months').format(),
      dateTo: moment().date(1).add(timespansFuture.months, 'months').format(),
      dataLoaded: false,
      exportSettingsDialog: false,
      selectedExportFormat: 0,
      selectedAssetTypeId: null,
      timespanFormat: 'MM/YYYY',
      timespanUnit: 'month',
      chartBarSelectedDataIndex: null,
      chartBarSelectedDatasetIndex: null,
      defaultChartBarOptions: [
        // closed past
        {
          bgColor: 'rgba(191, 191, 191, 0.15)',
          borderColor: 'rgba(191, 191, 191, 1)',
          hoverColor: 'rgba(191, 191, 191, 0.3)',
          borderWidth: 1
        },
        // open past
        {
          bgColor: 'rgba(75, 0, 130, 0.1)',
          borderColor: 'rgba(75, 0, 130, 0.7)',
          hoverColor: 'rgba(75, 0, 130, 0.15)',
          borderWidth: 1
        },
        // closed future
        {
          bgColor: 'rgba(115, 115, 115, 0.2)',
          borderColor: 'rgba(115, 115, 115, 1)',
          hoverColor: 'rgba(115, 115, 115, 0.35)',
          borderWidth: 1
        },
        // open future
        {
          bgColor: 'rgba(75, 0, 130, 0.2)',
          borderColor: 'rgba(75, 0, 130, 1)',
          hoverColor: 'rgba(75, 0, 130, 0.3)',
          borderWidth: 1
        }
      ],
      backgroundColors: { closedPast: [], openPast: [], closedFuture: [], openFuture: [] },
      borderColors: { closedPast: [], openPast: [], closedFuture: [], openFuture: [] },
      hoverColors: { closedPast: [], openPast: [], closedFuture: [], openFuture: [] },
      borderWidths: { closedPast: [], openPast: [], closedFuture: [], openFuture: [] }
    }
  },
  mounted () {
    this.downloadData()
  },
  computed: {
    polishedChartData () {
      const chartData = this.$store.state.reportItems
      const labels = chartData.map(({ timespan }) => moment(timespan, this.timespanFormat))
      const datasets = this.getDatasets(chartData)
      return { labels, datasets }
    },
    chartOptions () {
      const self = this

      const plugins = {
        datalabels: {
          anchor: 'end',
          align: 'bottom',
          font: { size: 14, weight: 'bold' },
          color: 'rgba(75, 0, 130, 1)',
          formatter: value => (value > 0) ? value : ''
        }
      }

      const scales = {
        xAxes: [{
          stacked: true,
          type: 'time',
          barThickness: 50, // (this.timespanUnit === 'week' ? 25 : 'flex'),

          time: {
            unit: this.timespanUnit,
            min: moment(this.dateFrom).format(),
            max: moment(this.dateTo).format(),
            displayFormats: {
              week: 'WW/YYYY',
              month: 'MM/YYYY',
              quarter: '[Q]Q/YYYY'
            }
          }
        }],
        yAxes: [{
          stacked: true,
          ticks: {
            beginAtZero: true
          }
        }]
      }

      return {
        plugins,
        scales,
        maintainAspectRatio: false,
        legend: {
          display: false
        },
        tooltips: {
          enabled: true,
          callbacks: {
            title: ([tooltipItem]) => moment(tooltipItem.xLabel, 'MMM D, YYYY, HH:mm:ss A', this.$i18n.locale).format(self.timespanFormat)
          }
        },
        onClick: function (event, data) {
          if (data.length) {
            const element = (this.getElementAtEvent(event))[0]
            const elementDatasetIndex = element._datasetIndex
            const elementDataIndex = element._index
            const dateFromLocal = element._model.label
            const dateToLocal = dateFromLocal.range(self.timespanUnit).end

            let colorArrayNameOld = ''
            switch (self.chartBarSelectedDatasetIndex) {
              case 0:
                colorArrayNameOld = 'closedPast'
                break
              case 1:
                colorArrayNameOld = 'openPast'
                break
              case 2:
                colorArrayNameOld = 'closedFuture'
                break
              case 3:
                colorArrayNameOld = 'openFuture'
                break
            }

            let colorArrayNameActual = ''
            switch (elementDatasetIndex) {
              case 0:
                colorArrayNameActual = 'closedPast'
                break
              case 1:
                colorArrayNameActual = 'openPast'
                break
              case 2:
                colorArrayNameActual = 'closedFuture'
                break
              case 3:
                colorArrayNameActual = 'openFuture'
                break
            }

            if (self.chartBarSelectedDatasetIndex !== null && self.chartBarSelectedDataIndex !== null) {
              self.backgroundColors[colorArrayNameOld][self.chartBarSelectedDataIndex] = self.defaultChartBarOptions[self.chartBarSelectedDatasetIndex].bgColor
              self.hoverColors[colorArrayNameOld][self.chartBarSelectedDataIndex] = self.defaultChartBarOptions[self.chartBarSelectedDatasetIndex].hoverColor
              self.borderWidths[colorArrayNameOld][self.chartBarSelectedDataIndex] = self.defaultChartBarOptions[self.chartBarSelectedDatasetIndex].borderWidth
            }

            if (self.chartBarSelectedDataIndex === elementDataIndex && self.chartBarSelectedDatasetIndex === elementDatasetIndex) {
              self.chartBarSelectedDataIndex = null
              self.chartBarSelectedDatasetIndex = null
              return
            }

            const oldColor = rgba(self.defaultChartBarOptions[elementDatasetIndex].bgColor)
            const newColor = 'rgba(' + oldColor[0] + ', ' + oldColor[1] + ', ' + oldColor[2] + ', 0.6)'
            self.backgroundColors[colorArrayNameActual][elementDataIndex] = newColor
            self.hoverColors[colorArrayNameActual][elementDataIndex] = newColor
            self.borderWidths[colorArrayNameActual][elementDataIndex] = 3
            this.update()

            self.selectData({ elementDataIndex, elementDatasetIndex }, { dateFromLocal, dateToLocal })
          }
        }
      }
    },
    assetTypesLocal () {
      return this.$store.state.items['asset-types']
    },
    issuesToExport () {
      return this.$store.state.items['issues']
    }
  },
  methods: {
    issuesFilter (status) {
      status === 'closed' ? this.$store.state.items['issues'] = this.$store.state.items['issues'].filter(issue => issue.closed !== null) : this.$store.state.items['issues'] = this.$store.state.items['issues'].filter(issue => issue.closed === null)
    },
    resetDatasetArrays (closedPastLength, openPastLength, closedFutureLength, openFutureLength) {
      // CLOSED PAST
      if (this.backgroundColors.closedPast.length) {
        this.backgroundColors.closedPast = []
        this.borderColors.closedPast = []
        this.hoverColors.closedPast = []
        this.borderWidths.closedPast = []
      }
      for (let i = 0; i < closedPastLength; i++) {
        this.backgroundColors.closedPast.push(this.defaultChartBarOptions[0].bgColor)
        this.borderColors.closedPast.push(this.defaultChartBarOptions[0].borderColor)
        this.hoverColors.closedPast.push(this.defaultChartBarOptions[0].hoverColor)
        this.borderWidths.closedPast.push(this.defaultChartBarOptions[0].borderWidth)
      }

      // OPEN PAST
      if (this.backgroundColors.openPast.length) {
        this.backgroundColors.openPast = []
        this.borderColors.openPast = []
        this.hoverColors.openPast = []
        this.borderWidths.openPast = []
      }
      for (let i = 0; i < openPastLength; i++) {
        this.backgroundColors.openPast.push(this.defaultChartBarOptions[1].bgColor)
        this.borderColors.openPast.push(this.defaultChartBarOptions[1].borderColor)
        this.hoverColors.openPast.push(this.defaultChartBarOptions[1].hoverColor)
        this.borderWidths.openPast.push(this.defaultChartBarOptions[1].borderWidth)
      }

      // CLOSED FUTURE
      if (this.backgroundColors.closedFuture.length) {
        this.backgroundColors.closedFuture = []
        this.borderColors.closedFuture = []
        this.hoverColors.closedFuture = []
        this.borderWidths.closedFuture = []
      }
      for (let i = 0; i < closedFutureLength; i++) {
        this.backgroundColors.closedFuture.push(this.defaultChartBarOptions[2].bgColor)
        this.borderColors.closedFuture.push(this.defaultChartBarOptions[2].borderColor)
        this.hoverColors.closedFuture.push(this.defaultChartBarOptions[2].hoverColor)
        this.borderWidths.closedFuture.push(this.defaultChartBarOptions[2].borderWidth)
      }

      // OPEN FUTURE
      if (this.backgroundColors.openFuture.length) {
        this.backgroundColors.openFuture = []
        this.borderColors.openFuture = []
        this.hoverColors.openFuture = []
        this.borderWidths.openFuture = []
      }
      for (let i = 0; i < openFutureLength; i++) {
        this.backgroundColors.openFuture.push(this.defaultChartBarOptions[3].bgColor)
        this.borderColors.openFuture.push(this.defaultChartBarOptions[3].borderColor)
        this.hoverColors.openFuture.push(this.defaultChartBarOptions[3].hoverColor)
        this.borderWidths.openFuture.push(this.defaultChartBarOptions[3].borderWidth)
      }
    },
    selectData (indexes, dates) {
      this.$store.dispatch('getItems', {
        model: { name: 'issues' },
        customParams: { 'dateFrom': dates.dateFromLocal.format(), 'dateTo': dates.dateToLocal.format(), closedIssues: true }
      }).then(() => {
        this.chartBarSelectedDataIndex = indexes.elementDataIndex
        this.chartBarSelectedDatasetIndex = indexes.elementDatasetIndex
      })
    },
    async downloadData (assetTypeId = null) {
      const { dateFrom, dateTo, timespanFormat } = this
      await Promise.all([
        this.$store.dispatch('downloadReport', {
          name: 'common-future-workload',
          dateFrom,
          dateTo,
          timespanFormat,
          assetTypeId
        }),
        this.$store.dispatch('getItems', { model: { name: 'asset-types' } })
      ])
      this.dataLoaded = true
    },
    async buildAssetReportCsv () {
      const assetTypeId = this.selectedAssetTypeId
      this.$store.dispatch('getItems', { model: { name: 'issues' }, customParams: { assetTypeId } }).then(() => {
        this.$nextTick(() => {
          const flatExportData = this.issuesToExport.reduce((carry, issue) => {
            return [...carry, {
              'asset.id': issue.asset.id,
              'asset.name': issue.asset.name,
              'issue.code': issue.code,
              'issue.name': issue.name,
              'issue.actionAt': issue.actionAt,
              'issue.closed': issue.closed
            }]
          }, [])
          const parser = new json2csv.Parser({ withBOM: true, fields: ['asset.id', 'asset.name', 'issue.code', 'issue.name', 'issue.actionAt', 'issue.closed'] })
          const csvData = parser.parse(flatExportData)

          this.downloadCsvFile(csvData)
        })
      })
    },
    async buildSummaryReportCsv () {
      const { dateFrom, dateTo } = this
      this.$store.dispatch('getItems', { model: { name: 'issues' }, customParams: { dateFrom, dateTo } }).then(() => {
        this.$nextTick(() => {
          let fields = ['timespan', 'summary']
          const flatExportData = this.issuesToExport.reduce((carry, issue) => {
            const issueTimespan = moment(issue.actionAt).format(this.timespanFormat)

            let timespanRow = carry.find(e => e.timespan === issueTimespan)

            if (issue.asset.assetType) {
              if (!timespanRow) {
                timespanRow = { 'timespan': issueTimespan, 'summary': 1, [issue.asset.assetType.name]: 1 }

                return [...carry, timespanRow]
              }
              if (!timespanRow[issue.asset.assetType.name]) {
                timespanRow[issue.asset.assetType.name] = 1
              } else {
                timespanRow[issue.asset.assetType.name]++
              }
              if (!fields.includes(issue.asset.assetType.name)) fields = [...fields, issue.asset.assetType.name]
              timespanRow.summary++
            }

            carry.sort((a, b) => {
              return moment(a.timespan, this.timespanFormat).diff(moment(b.timespan, this.timespanFormat))
            })

            return [...carry]
          }, [])

          flatExportData.reduce((carry, row) => {
            fields.forEach(e => {
              row[e] = row[e] || 0
            })
            return [...carry, row]
          }, [])

          const parser = new json2csv.Parser({ withBOM: true, fields })
          const csvData = parser.parse(flatExportData)

          this.downloadCsvFile(csvData, 'summary')
        })
      })
    },
    downloadCsvFile (csvData, reportKind) {
      const filename = (reportKind || 'assetType-' + this.selectedAssetTypeId) + '_future-workload-report'
      const link = document.createElement('a')
      document.body.appendChild(link)
      link.setAttribute('href', 'data:text/csv;charset=utf-8,\uFEFF' + encodeURI(csvData))
      link.setAttribute('download', `${filename}.csv`)
      link.click()
      document.body.removeChild(link)
    },
    getDatasets (chartData) {
      const self = this
      const currentTimespan = moment()

      // TODO Is chartData.filter().map() better?
      const closedPastData = chartData.map(({ timespan, closed }) => {
        if (moment(timespan, self.timespanFormat).isBefore(currentTimespan)) {
          return closed
        }
      })
      const openPastData = chartData.map(({ timespan, opened }) => {
        if (moment(timespan, self.timespanFormat).isBefore(currentTimespan)) {
          return opened
        }
      })
      const closedFutureData = chartData.map(({ timespan, closed }) => {
        if (moment(timespan, self.timespanFormat).isSameOrAfter(currentTimespan)) {
          return closed
        }
      })
      const openFutureData = chartData.map(({ timespan, opened }) => {
        if (moment(timespan, self.timespanFormat).isSameOrAfter(currentTimespan)) {
          return opened
        }
      })

      if (self.chartBarSelectedDataIndex == null) {
        self.resetDatasetArrays(closedPastData.length, openPastData.length, closedFutureData.length, openFutureData.length)
      }

      const datasets = [
        {
          label: 'Closed', // past
          backgroundColor: self.backgroundColors.closedPast,
          borderColor: self.borderColors.closedPast,
          hoverBackgroundColor: self.hoverColors.closedPast,
          borderWidth: self.borderWidths.closedPast,
          datalabels: {
            color: 'rgba(140, 140, 140, 1)'
          },
          data: closedPastData
        },
        {
          label: 'Open', // past
          backgroundColor: self.backgroundColors.openPast,
          borderColor: self.borderColors.openPast,
          hoverBackgroundColor: self.hoverColors.openPast,
          borderWidth: self.borderWidths.openPast,
          data: openPastData
        },
        {
          label: 'Closed', // future
          backgroundColor: self.backgroundColors.closedFuture,
          borderColor: self.borderColors.closedFuture,
          hoverBackgroundColor: self.hoverColors.closedFuture,
          borderWidth: self.borderWidths.closedFuture,
          datalabels: {
            color: 'rgba(115, 115, 115, 1)'
          },
          data: closedFutureData
        },
        {
          label: 'Open', // future
          backgroundColor: self.backgroundColors.openFuture,
          borderColor: self.borderColors.openFuture,
          hoverBackgroundColor: self.hoverColors.openFuture,
          borderWidth: self.borderWidths.openFuture,
          data: openFutureData
        }]

      return datasets
    }
  },
  watch: {
    chartBarSelectedDatasetIndex () {
      if (this.chartBarSelectedDatasetIndex === 0) {
        this.issuesFilter('closed')
      } else if (this.chartBarSelectedDatasetIndex === 1) {
        this.issuesFilter('open')
      } else if (this.chartBarSelectedDatasetIndex === 2) {
        this.issuesFilter('closed')
      } else if (this.chartBarSelectedDatasetIndex === 3) {
        this.issuesFilter('open')
      }
    },
    selectedAssetTypeId (val) {
      clearTimeout(this.to)
      this.to = setTimeout(() => this.downloadData(val), 100)
    },
    dateFrom () {
      clearTimeout(this.to)
      this.to = setTimeout(() => this.downloadData(this.selectedAssetTypeId), 100)
    },
    dateTo () {
      clearTimeout(this.to)
      this.to = setTimeout(() => this.downloadData(this.selectedAssetTypeId), 100)
    },
    timespanFormat (val) {
      if (val === 'MM/YYYY') {
        this.timespanUnit = 'month'
        this.dateFrom = moment().date(1).subtract(timespansPast.months, 'month').format()
        this.dateTo = moment().date(1).add(timespansFuture.months, 'month').format()
      } else if (val === 'WW/YYYY') {
        this.timespanUnit = 'week'
        this.dateFrom = moment().date(1).subtract(timespansPast.weeks, 'week').format()
        this.dateTo = moment().date(1).add(timespansFuture.weeks, 'week').format()
      } else if (val === '[Q]Q/YYYY') {
        this.timespanUnit = 'quarter'
        this.dateFrom = moment().date(1).subtract(timespansPast.quarters, 'quarter').format()
        this.dateTo = moment().date(1).add(timespansFuture.quarters, 'quarter').format()
      } else {
        this.to = setTimeout(() => this.downloadData(this.selectedAssetTypeId), 100)
      }
    }
  },
  components: {
    CommonFutureWorkloadChart,
    DateTimePicker,
    IssuesList
  }
}
</script>
